import { useRef, useState } from 'react';
import { useMountEffect, useStore } from '../lib/hooks';
import { formatTick, formatTimecode } from '../lib/format';
import { effectiveInPoint, snapToFrame } from '../lib/timeline';
import type { Recording } from '../lib/engine';
import type { ClipPlayer } from '../lib/player';

const HEIGHT = 180;
const RULER_H = 24;
const FILM_TOP = 32;
const FILM_H = 68;
const WAVE_TOP = 106;
const WAVE_H = 44;
const BOTTOM = 158;
const HANDLE_GRAB_PX = 9;

interface TrimRange {
  inUs: number;
  outUs: number;
}

interface DrawParams {
  recording: Recording;
  trim: TrimRange;
  currentUs: number;
  snap: boolean;
  hoverUs: number | null;
}

type DragKind = 'in' | 'out' | 'scrub';

/**
 * The editing surface: ruler, keyframe strip, waveform envelope, trim handles
 * and playhead, all drawn on one canvas so scrubbing stays smooth.
 *
 * The static layers (ruler, filmstrip, waveform, selection) are rendered once
 * into an offscreen "bed"; each frame only blits the bed and draws the playhead.
 */
class TimelineRenderer {
  private bed: HTMLCanvasElement;
  private bedKey = '';
  private images = new Map<string, HTMLImageElement>();
  private requestRedraw: () => void;
  private loadedImages = 0;

  constructor(requestRedraw: () => void) {
    this.requestRedraw = requestRedraw;
    this.bed = document.createElement('canvas');
  }

  private image(url: string): HTMLImageElement | null {
    const existing = this.images.get(url);
    if (existing) return existing.complete ? existing : null;
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    image.onload = () => {
      this.loadedImages += 1;
      this.requestRedraw();
    };
    this.images.set(url, image);
    return null;
  }

  draw(canvas: HTMLCanvasElement, cssWidth: number, cssHeight: number, params: DrawParams): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round(cssWidth));
    const height = Math.max(1, Math.round(cssHeight));
    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const key = [
      width,
      height,
      params.recording.id,
      Math.round(params.trim.inUs),
      Math.round(params.trim.outUs),
      params.recording.thumbs.length,
      params.recording.peaks ? params.recording.peaks.length : 0,
      // Image loads land after the first paint, so they must invalidate the bed.
      this.loadedImages,
    ].join(':');

    if (this.bedKey !== key) {
      this.paintBed(width, height, params);
      this.bedKey = key;
    }

    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(this.bed, 0, 0, width, height);
    this.paintHover(ctx, width, params);
    this.paintPlayhead(ctx, width, height, params);
    ctx.restore();
  }

  private usToX(us: number, width: number, durationUs: number): number {
    if (durationUs <= 0) return 0;
    return (us / durationUs) * width;
  }

  private paintBed(width: number, height: number, params: DrawParams): void {
    const { recording } = params;
    const durationUs = recording.durationUs || 1;
    this.bed.width = width;
    this.bed.height = height;
    const ctx = this.bed.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#0b0d10';
    ctx.fillRect(0, 0, width, height);

    // Ruler -----------------------------------------------------------------
    ctx.fillStyle = '#08090b';
    ctx.fillRect(0, 0, width, RULER_H);
    const stepUs = this.chooseTickStep(durationUs, width);
    ctx.font = '500 10px "DM Mono", ui-monospace, monospace';
    ctx.textBaseline = 'middle';
    for (let us = 0; us <= durationUs; us += stepUs) {
      const x = Math.round(this.usToX(us, width, durationUs)) + 0.5;
      ctx.strokeStyle = 'rgba(232,236,244,0.16)';
      ctx.beginPath();
      ctx.moveTo(x, RULER_H - 7);
      ctx.lineTo(x, RULER_H);
      ctx.stroke();
      if (x + 46 < width) {
        ctx.fillStyle = 'rgba(154,162,174,0.85)';
        ctx.fillText(formatTick(us, stepUs), x + 5, RULER_H / 2 - 1);
      }
    }

    // Filmstrip -------------------------------------------------------------
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, FILM_TOP, width, FILM_H);
    ctx.clip();

    const gradient = ctx.createLinearGradient(0, FILM_TOP, width, FILM_TOP + FILM_H);
    gradient.addColorStop(0, '#151a21');
    gradient.addColorStop(1, '#101318');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, FILM_TOP, width, FILM_H);

    const thumbs = recording.thumbs;
    if (thumbs.length > 0) {
      for (let i = 0; i < thumbs.length; i += 1) {
        const thumb = thumbs[i];
        const next = thumbs[i + 1];
        const startX = this.usToX(thumb.tsUs, width, durationUs);
        const endX = next ? this.usToX(next.tsUs, width, durationUs) : width;
        const blockWidth = Math.max(2, endX - startX);
        const image = this.image(thumb.url);
        if (image) {
          ctx.drawImage(image, startX, FILM_TOP, blockWidth, FILM_H);
        }
        ctx.fillStyle = 'rgba(8,9,11,0.55)';
        ctx.fillRect(startX - 0.5, FILM_TOP, 1, FILM_H);
      }
      ctx.fillStyle = 'rgba(8,9,11,0.16)';
      ctx.fillRect(0, FILM_TOP, width, FILM_H);
    } else {
      ctx.fillStyle = 'rgba(154,162,174,0.28)';
      ctx.font = '500 10px "DM Mono", ui-monospace, monospace';
      ctx.textAlign = 'center';
      ctx.fillText('INDEXING FRAMES…', width / 2, FILM_TOP + FILM_H / 2);
      ctx.textAlign = 'left';
    }

    // Keyframe ticks: the cuts that stay lossless.
    ctx.fillStyle = 'rgba(255, 178, 87, 0.55)';
    for (const index of recording.keyframes) {
      const x = Math.round(this.usToX(recording.video.timeline[index].timestamp, width, durationUs));
      ctx.fillRect(x, FILM_TOP, 1, 5);
    }
    ctx.restore();

    ctx.strokeStyle = 'rgba(232,236,244,0.08)';
    ctx.beginPath();
    ctx.moveTo(0, FILM_TOP + 0.5);
    ctx.lineTo(width, FILM_TOP + 0.5);
    ctx.moveTo(0, FILM_TOP + FILM_H + 0.5);
    ctx.lineTo(width, FILM_TOP + FILM_H + 0.5);
    ctx.stroke();

    // Waveform --------------------------------------------------------------
    ctx.fillStyle = '#0a0c0f';
    ctx.fillRect(0, WAVE_TOP, width, WAVE_H);
    const peaks = recording.peaks;
    const centre = WAVE_TOP + WAVE_H / 2;
    if (peaks && peaks.length > 0) {
      const scale = WAVE_H / 2 - 3;
      ctx.fillStyle = 'rgba(88, 224, 208, 0.6)';
      for (let x = 0; x < width; x += 1) {
        const from = Math.floor((x / width) * peaks.length);
        const to = Math.max(from + 1, Math.floor(((x + 1) / width) * peaks.length));
        let peak = 0;
        for (let i = from; i < to && i < peaks.length; i += 1) {
          if (peaks[i] > peak) peak = peaks[i];
        }
        const amplitude = Math.max(0.016, Math.min(1, peak)) * scale;
        ctx.fillRect(x, centre - amplitude, 1, amplitude * 2);
      }
    } else {
      ctx.strokeStyle = 'rgba(232,236,244,0.14)';
      ctx.beginPath();
      ctx.moveTo(0, centre + 0.5);
      ctx.lineTo(width, centre + 0.5);
      ctx.stroke();
    }

    // Selection -------------------------------------------------------------
    const inX = this.usToX(params.trim.inUs, width, durationUs);
    const outX = this.usToX(params.trim.outUs, width, durationUs);
    ctx.fillStyle = 'rgba(8,9,11,0.66)';
    ctx.fillRect(0, 0, inX, height);
    ctx.fillRect(outX, 0, width - outX, height);
    ctx.fillStyle = 'rgba(255, 178, 87, 0.05)';
    ctx.fillRect(inX, 0, outX - inX, height);

    this.paintHandle(ctx, inX, true);
    this.paintHandle(ctx, outX, false);

    ctx.fillStyle = 'rgba(255, 178, 87, 0.5)';
    ctx.fillRect(Math.round(inX), RULER_H, Math.max(1, Math.round(outX - inX)), 2);
  }

  private paintHandle(ctx: CanvasRenderingContext2D, x: number, isIn: boolean): void {
    const clamped = Math.round(x) + 0.5;
    ctx.strokeStyle = 'rgba(255, 178, 87, 0.9)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(clamped, 8);
    ctx.lineTo(clamped, BOTTOM);
    ctx.stroke();

    const capY = 2;
    const capWidth = 7;
    const capHeight = 16;
    const capX = isIn ? clamped : clamped - capWidth;
    ctx.fillStyle = '#ffb257';
    ctx.beginPath();
    const r = 2;
    ctx.moveTo(capX + r, capY);
    ctx.lineTo(capX + capWidth - r, capY);
    ctx.quadraticCurveTo(capX + capWidth, capY, capX + capWidth, capY + r);
    ctx.lineTo(capX + capWidth, capY + capHeight - r);
    ctx.quadraticCurveTo(capX + capWidth, capY + capHeight, capX + capWidth - r, capY + capHeight);
    ctx.lineTo(capX + r, capY + capHeight);
    ctx.quadraticCurveTo(capX, capY + capHeight, capX, capY + capHeight - r);
    ctx.lineTo(capX, capY + r);
    ctx.quadraticCurveTo(capX, capY, capX + r, capY);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = 'rgba(11,13,16,0.85)';
    ctx.fillRect(capX + 2.5, capY + 5, 1, 6);
    ctx.fillRect(capX + 4.5, capY + 5, 1, 6);
  }

  private paintHover(ctx: CanvasRenderingContext2D, width: number, params: DrawParams): void {
    if (params.hoverUs === null) return;
    const x = Math.round(this.usToX(params.hoverUs, width, params.recording.durationUs || 1)) + 0.5;
    ctx.strokeStyle = 'rgba(232,236,244,0.22)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, RULER_H);
    ctx.lineTo(x, BOTTOM);
    ctx.stroke();
    ctx.fillStyle = 'rgba(232,236,244,0.75)';
    ctx.font = '500 10px "DM Mono", ui-monospace, monospace';
    const label = formatTimecode(params.hoverUs, false);
    const textWidth = ctx.measureText(label).width;
    ctx.fillRect(x - textWidth / 2 - 4, BOTTOM + 4, textWidth + 8, 14);
    ctx.fillStyle = '#0b0d10';
    ctx.fillText(label, x - textWidth / 2, BOTTOM + 11.5);
  }

  private paintPlayhead(ctx: CanvasRenderingContext2D, width: number, height: number, params: DrawParams): void {
    const x = Math.round(this.usToX(params.currentUs, width, params.recording.durationUs || 1)) + 0.5;
    ctx.strokeStyle = '#ff4b2b';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, RULER_H);
    ctx.lineTo(x, height);
    ctx.stroke();

    ctx.fillStyle = '#ff4b2b';
    ctx.beginPath();
    ctx.moveTo(x - 5, RULER_H - 7);
    ctx.lineTo(x + 5, RULER_H - 7);
    ctx.lineTo(x, RULER_H + 1);
    ctx.closePath();
    ctx.fill();
  }

  private chooseTickStep(durationUs: number, width: number): number {
    const steps = [0.25, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800];
    const target = 86;
    for (const seconds of steps) {
      const stepUs = seconds * 1_000_000;
      if ((stepUs / Math.max(durationUs, 1)) * width >= target) return stepUs;
    }
    return 1800 * 1_000_000;
  }
}

export function Timeline({
  recording,
  player,
  trim,
  onChange,
  snap,
  indexing,
}: {
  recording: Recording;
  player: ClipPlayer;
  trim: TrimRange;
  onChange: (trim: TrimRange) => void;
  snap: boolean;
  indexing: boolean;
}) {
  const playerState = useStore(player.store);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragKind | null>(null);
  const sizeRef = useRef({ width: 0, height: HEIGHT });
  const [, setVersion] = useState(0);
  const [renderer] = useState(() => new TimelineRenderer(() => setVersion((value) => value + 1)));
  const hoverRef = useRef<number | null>(null);

  const params: DrawParams = {
    recording,
    trim,
    currentUs: playerState.currentUs,
    snap,
    hoverUs: hoverRef.current,
  };
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    renderer.draw(canvas, sizeRef.current.width, sizeRef.current.height, paramsRef.current);
  };

  // Also drawn on every render below, which covers prop and playhead changes.
  useMountEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      sizeRef.current = { width: rect.width, height: HEIGHT };
      draw();
    });
    observer.observe(wrap);
    sizeRef.current = { width: wrap.clientWidth, height: HEIGHT };
    draw();
    return () => observer.disconnect();
  });

  draw();

  const xToUs = (clientX: number): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const rect = canvas.getBoundingClientRect();
    const ratio = (clientX - rect.left) / Math.max(rect.width, 1);
    return Math.max(0, Math.min(recording.durationUs, ratio * recording.durationUs));
  };

  const snapValue = (us: number, kind: DragKind): number => {
    if (kind === 'scrub') return snapToFrame(us, recording.frameUs);
    if (!snap) return snapToFrame(us, recording.frameUs);
    const frames = recording.video.timeline;
    let best = us;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const index of recording.keyframes) {
      const ts = frames[index].timestamp;
      const distance = Math.abs(ts - us);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = ts;
      }
    }
    const threshold = recording.durationUs / Math.max(sizeRef.current.width, 1) * 9;
    return bestDistance <= threshold ? best : snapToFrame(us, recording.frameUs);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(event.pointerId);
    const rect = canvas.getBoundingClientRect();
    const us = xToUs(event.clientX);
    const inX = (trim.inUs / recording.durationUs) * rect.width;
    const outX = (trim.outUs / recording.durationUs) * rect.width;
    const localX = event.clientX - rect.left;
    if (Math.abs(localX - inX) <= HANDLE_GRAB_PX) dragRef.current = 'in';
    else if (Math.abs(localX - outX) <= HANDLE_GRAB_PX) dragRef.current = 'out';
    else {
      dragRef.current = 'scrub';
      player.pause();
      player.seekUs(snapToFrame(us, recording.frameUs));
    }
    draw();
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const us = xToUs(event.clientX);
    hoverRef.current = us;
    const kind = dragRef.current;
    if (!kind) {
      draw();
      return;
    }
    if (kind === 'scrub') {
      player.seekUs(snapToFrame(us, recording.frameUs));
      return;
    }
    const value = snapValue(us, kind);
    if (kind === 'in') {
      const nextIn = Math.max(0, Math.min(value, trim.outUs - recording.frameUs));
      onChange({ inUs: nextIn, outUs: trim.outUs });
      player.seekUs(nextIn);
    } else {
      const nextOut = Math.min(recording.durationUs, Math.max(value, trim.inUs + recording.frameUs));
      onChange({ inUs: trim.inUs, outUs: nextOut });
      player.seekUs(nextOut);
    }
  };

  const endDrag = (event: React.PointerEvent<HTMLCanvasElement>) => {
    dragRef.current = null;
    canvasRef.current?.releasePointerCapture(event.pointerId);
    draw();
  };

  const handlePointerLeave = () => {
    hoverRef.current = null;
    draw();
  };

  const losslessStart = effectiveInPoint(recording.video.timeline, recording.keyframes, trim.inUs);
  const keyframeAligned = losslessStart <= trim.inUs + recording.frameUs;
  const stills = recording.thumbs.length;
  const waveformReady = Boolean(recording.peaks && recording.peaks.length > 0);

  return (
    <div
      className="timeline"
      data-stills={stills}
      data-waveform={waveformReady ? 'ready' : 'pending'}
      data-indexing={indexing ? 'true' : 'false'}
    >
      <div className="timeline__canvas" ref={wrapRef}>
        <canvas
          ref={canvasRef}
          height={HEIGHT}
          style={{ height: HEIGHT }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={handlePointerLeave}
        />
      </div>
      <div className="timeline__foot">
        <span className="timeline__legend">
          <i className="legend-dot legend-dot--key" /> keyframe
          <i className="legend-dot legend-dot--audio" /> audio
        </span>
        {indexing ? (
          <span className="timeline__note">indexing frames…</span>
        ) : keyframeAligned ? (
          <span className="timeline__note">cut sits on a keyframe — lossless</span>
        ) : (
          <span className="timeline__note">
            lossless cut starts at {formatTimecode(losslessStart, false)}
          </span>
        )}
      </div>
    </div>
  );
}
